import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { SidebarMenuButton } from '@/components/ui/sidebar'
import { useGetUserOrgs } from '@/services/auth'
import { UserOrg } from '@/services/inference.gen'
import { hasAdminPrivileges } from '@/services/utils'
import { CheckIcon, ChevronRight, Home } from 'lucide-react'
import { useActiveOrgId } from '@/hooks/useActiveOrgId'
import { useNavigate } from '@tanstack/react-router'
import { useActiveOrgProfile } from '@/hooks/useActiveOrgProfile'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { cn } from '@/lib/utils'
import { LoadingMessage } from '@/components/loading-message'
import { ErrorMessage } from '@/components/error-message'

export const SwitchOrg = () => {
  const { data: activeOrg, isLoading, isError } = useActiveOrgProfile()
  const {
    data: userOrgs,
    isLoading: isUserOrgsLoading,
    isError: isUserOrgsError,
  } = useGetUserOrgs()
  const { orgId: activeOrgId, setOrgId } = useActiveOrgId()
  const navigate = useNavigate()

  const isPublicSignupEnabled =
    import.meta.env.VITE_ENABLE_PUBLIC_SIGNUP === 'true'

  if (isError || isUserOrgsError) {
    return (
      <ErrorMessage
        message="Unable to load organizations"
        className="text-sm p-3 bg-destructive/10 rounded-md border border-destructive/30"
      />
    )
  }

  if (isUserOrgsLoading) {
    return (
      <div className="flex items-center justify-center w-full min-h-[72px] bg-muted/30 rounded-md border border-border/50">
        <LoadingMessage
          message="Loading organizations..."
          centered
          className="my-4"
        />
      </div>
    )
  }

  if (!userOrgs?.orgs?.length) {
    return (
      <div className="flex items-center justify-center w-full min-h-[72px] bg-muted/30 rounded-md border border-border/50">
        <p className="text-sm text-muted-foreground">
          You are not a member of any organizations.
        </p>
      </div>
    )
  }

  // Filter out default orgs when public signup is disabled
  const filteredOrgs = isPublicSignupEnabled
    ? userOrgs.orgs
    : userOrgs.orgs.filter((org) => !org.org?.is_default_org)

  if (!filteredOrgs.length) {
    return (
      <div className="flex items-center justify-center w-full min-h-[72px] bg-muted/30 rounded-md border border-border/50">
        <p className="text-sm text-muted-foreground">
          You are not a member of any organizations.
        </p>
      </div>
    )
  }

  const switchOrg = (orgId: string) => {
    localStorage.setItem('lastAccessedOrgId', orgId)
    setOrgId(Number(orgId))
    navigate({
      to: '/$orgId/dashboard',
      params: { orgId },
    })
  }

  const onlyOneOrg = filteredOrgs.length === 1

  if (onlyOneOrg) {
    return (
      <SidebarMenuButton
        disabled={isLoading}
        className="rounded-md min-h-[72px] border w-full bg-muted/30 border-border/50"
      >
        <OrgDisplay org={activeOrg} />
      </SidebarMenuButton>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <SidebarMenuButton
          disabled={isLoading}
          className="rounded-md min-h-[72px] border w-full bg-muted/30 border-border/50 hover:bg-muted/50"
        >
          <OrgDisplay org={activeOrg} />
          <ChevronRight className="ml-auto h-4 w-4 text-muted-foreground" />
        </SidebarMenuButton>
      </DropdownMenuTrigger>

      <DropdownMenuContent side="right" align="center">
        <div className="px-3 py-2 text-sm font-medium border-b border-border/50 bg-muted/20">
          Switch Organization
        </div>
        <div className="py-2">
          {filteredOrgs.map((org: UserOrg) => (
            <DropdownMenuItem
              key={org.org.org_id}
              className="flex items-center gap-3 py-2 px-3"
              onSelect={() => switchOrg(org.org.org_id.toString())}
            >
              <OrgDisplay org={org} />
              {activeOrgId === org.org.org_id && (
                <CheckIcon className="ml-auto h-4 w-4 text-primary" />
              )}
            </DropdownMenuItem>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

const OrgDisplay = ({ org }: { org: UserOrg | undefined }) => {
  if (!org) return null

  const isDefault = org.org?.is_default_org ?? false
  const name = org.org?.name || ''
  const avatarUrl = org.org?.profile_metadata?.avatar_preview_url || ''
  const roleName = org.user_role?.role_name || ''

  return (
    <div className="flex items-center gap-3 w-full">
      <div className="relative flex-shrink-0">
        <Avatar className="h-8 w-8 border border-border/50">
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={name}
              className="w-full h-full object-cover rounded-full"
            />
          ) : (
            <AvatarFallback className="font-medium text-xs">
              {name?.[0]?.toUpperCase()}
            </AvatarFallback>
          )}
        </Avatar>
        {isDefault && (
          <div className="absolute -top-1 -right-1 w-3 h-3 bg-primary rounded-full flex items-center justify-center">
            <Home className="w-2 h-2 text-primary-foreground" />
          </div>
        )}
      </div>
      <div className="flex flex-col flex-grow overflow-hidden min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm truncate">{name}</span>
          {isDefault && (
            <span className="text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded-full font-medium">
              Default
            </span>
          )}
        </div>
        <div className="text-xs text-muted-foreground flex items-center gap-1.5">
          <span
            className={cn(
              'inline-block w-1.5 h-1.5 rounded-full',
              hasAdminPrivileges(roleName) ? 'bg-primary' : 'bg-amber-500'
            )}
          />
          <span className="truncate">{roleName}</span>
        </div>
      </div>
    </div>
  )
}
